import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {funcOrValOr, minMax} from './CommonJS';
import {componentInit, getElmOfRef, OverlayUnderpanel, useEffectTransition, useEventListener, useStateEx} from './CommonReact';

export function usePopPanel(props) {
	const ctrl = useRef({}).current;

	if (componentInit(ctrl, props && [...Object.values(props)]))
		ctrl.panelJsx = <UIPopPanel {...{ctrl, ...props}}/>;

	return {panelJsx: ctrl.panelJsx, panelCtrl: ctrl};
}

export const enumOutClickModes = {
	noPropagate:    Symbol('outside click while panel is open will close panel and click will not propagate (default).'),
	propagate:      Symbol('...close panel and propagate.'),
	propagateNoAnchor:  Symbol('...close panel and propagate unless click is on rAnchor.'),
};

export function UIPopPanel({
	show, onClose, onShow, rAnchor, align, margin, ctrl, children, className, absPos, transition, classNameOverlay,
	onAfterTransition, onHideDone, outClickMode
}) {
	const [alt, setAlt] = useState({});             // show, rAnchor, content, className
	const {state: st, rerender} = useStateEx({closePanel: (...args) => st.closePanel.checkClose?.(...args) !== false && setAlt({show: false})});
	const isShown = () => (alt.show ?? show) && !st.forceClose, shown = !!isShown();
	st.forceClose = st.forceClose && (alt.show ?? show);
	if (alt.hasOwnProperty('children'))
		children = alt.children;
	rAnchor = alt.rAnchor ?? rAnchor;
	outClickMode = alt.outClickMode ?? outClickMode;
	const {classNameTrans, propsTrans, refTrans} = useEffectTransition({transition: alt.transition ?? transition,
		isShown: shown,
		onAfter: (e, stage) => (onAfterTransition?.(e, stage), stage < 4? layoutPanel() : onHideDone?.())});

	if (ctrl)
		Object.assign(ctrl, {set: setAlt, isShown});

	useEffect(() => { if (shown) onShow?.(); }, [shown]);
	useEffect(() => { if (shown && st.rPanel) rerender(); }, [st.rPanel]);      // enables useOutsideClick to run once rPanel is set.

	useOutsideClick({rPanel: st.rPanel,
		rAnchor,
		isEnabled: shown,
		outClickMode,
		onOutside: (e, sEv) => {
			if (sEv === 'mousedown')
				e.preventDefault();
			if (!outClickMode || outClickMode === enumOutClickModes.noPropagate)
				e.stopPropagation();
			if (st.closePanel.checkClose?.(sEv) === false ||
                (sEv === 'wheel' && classNameOverlay)) return;
			if (onClose) return onClose();
			st.forceClose = true;         // mechanism when user clicked outside, but no external way to close panel.
			setAlt({});
		}});

	function layoutPanel() {
		if (absPos || !classNameTrans) return;
		if (rAnchor instanceof Element && !document.body.contains(rAnchor))         // rAnchor disappeared? usually during transition.
			return setAlt({});

		const rEl = getElmOfRef(rAnchor)?.getBoundingClientRect() ?? rAnchor;
		const rPan = getElmOfRef(st.rPanel)?.getBoundingClientRect();
		if (!rPan?.width || typeof rEl?.x !== 'number') return;

		align = alt.align ?? align;
		margin = alt.margin ?? margin ?? 0;
		const mX = typeof margin === 'object' ? (margin.x ?? 0) : 0;
		const mY = typeof margin === 'object' ? (margin.y ?? 0) : margin;

		const left = minMax((align?.includes('right') ? rEl.x + rEl.width - rPan.width : rEl.x) + mX,
																						0, document.body.clientWidth - rPan.width);
		let top = minMax(rEl.y + (align?.includes('above')? -rPan.height - mY : (rEl?.height ?? 0) + mY),
																			0, document.body.clientHeight - rPan.height - mY);
		if (top < rEl.y + (rEl?.height ?? 0) + mY && top + rPan.height >= rEl.y)     // Clamped past margin? (negative = intentional overlap, not a flip).
			top = minMax(rEl.y + (!align?.includes('above')? -rPan.height - mY : (rEl?.height ?? 0) + mY),
																0, document.body.clientHeight - rPan.height - mY);

		Object.assign(st.rPanel.style, {left: left +'px', top: top +'px'});
	}

	useLayoutEffect(layoutPanel, [rAnchor, st.rPanel, children, align, margin, alt]);

	return createPortal(
		<OverlayUnderpanel {...{classNameOverlay, classNameTrans, onClose}}>
			{() => (
				<div
					ref={e => (refTrans(e), st.rPanel = e)} {...propsTrans}
					className={`${classNameTrans} ${
						absPos && (typeof absPos === 'string'? absPos : 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2')} ${
						className ?? alt.className}`}
					style={{position: absPos? 'absolute' : 'fixed', zIndex: 50 + (absPos? 0 : 50)}} data-test='pop-panel'>
					{(!classNameOverlay || shown) && funcOrValOr(children, {closePanel: st.closePanel})}
				</div>
			)}
		</OverlayUnderpanel>,
		document.body);
}

const nestedPanels = [];                                                              // list of all panels currently open
const blockingPanels = new WeakSet();                                                // panels that block keyboard shortcuts (not tooltips/hovers)
export const hasOpenPanel = () => nestedPanels.some(p => blockingPanels.has(p));

function stopPropagation(e) {
	e.stopPropagation();
	e.preventDefault();
}

function useOutsideClick({
	rPanel, rAnchor, onOutside, isEnabled = true, outClickMode = enumOutClickModes.noPropagate
}) {
	const vals = useRef({}).current;
	const isBlocking = outClickMode !== enumOutClickModes.propagate;

	if (vals.rPanel !== rPanel || vals.isEnabled !== !!isEnabled) {
		if (!rPanel || !isEnabled) {
			blockingPanels.delete(vals.rPanel);
			nestedPanels.removeFirst(vals.rPanel);
		} else if (vals.rPanel) {
			blockingPanels.delete(vals.rPanel);
			nestedPanels.removeFirst(vals.rPanel, rPanel);          // replace
			if (isBlocking) blockingPanels.add(rPanel);
		} else {
			nestedPanels.push(rPanel);
			if (isBlocking) blockingPanels.add(rPanel);
		}

		vals.rPanel = isEnabled && rPanel;
		vals.isEnabled = !!isEnabled;
	}

	useEffect(() => () => { blockingPanels.delete(vals.rPanel); nestedPanels.removeFirst(vals.rPanel); }, []);       // on destruction, ensure removed.

	for (const sEv of ['mousedown', 'wheel', 'touchmove'])
		useEventListener({
			eOn: window,
			sEv,
			captureOrOpts: true,
			isEnabled,
			cb: e => {
				const target = e.composedPath?.()?.[0] || e.target;
				if (nestedPanels.last === vals.rPanel &&                                // rPanel is the latest in nesting
                    target?.getRootNode().contains(target) &&                           // target still exists in DOM (not sure why?)
                    !getElmOfRef(vals.rPanel)?.contains(target)) {                      // target is not under rPanel

					onOutside(e, sEv);

                    // if clicked outside, and not requested to allow propagation or clicked on the rAnchor, then suppress click.
					if (sEv === 'mousedown' &&
                        (outClickMode === enumOutClickModes.noPropagate ||
                            (outClickMode === enumOutClickModes.propagateNoAnchor && getElmOfRef(rAnchor)?.contains(target)))) {
						window.addEventListener('click', stopPropagation, true);
						window.addEventListener('contextmenu', stopPropagation, true);
						window.addEventListener('mouseup', e => setTimeout(() => {          // allow time for click or contextmenu to shoot before cancelling their handlers
							window.removeEventListener('click', stopPropagation, true);
							window.removeEventListener('contextmenu', stopPropagation, true);
						}), {capture: true, once: true});
					}
				}
			}
		});

	useEventListener({
		eOn: window,
		sEv: 'keydown',
		isEnabled,
		cb: e => {
			if (e.key === 'Escape' && nestedPanels.last === vals.rPanel) {
				e.stopPropagation();
				e.preventDefault();
				onOutside(e, 'keydown');
			}
		}
	});
}
